import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import useMediaQuery from "@mui/material/useMediaQuery";
import { UseUserDetails } from "../../hooks/auth";
import { UseCompanyColors } from "../../hooks/company";
import { GET_IMAGE } from "../../helpers/images";
import useHideOnScroll from "../../hooks/useHideOnScroll";

import "./bottomNavStyles.css";

// Same neutral/typography tokens FeedCard and YardSignPost already use —
// keeps the redesigned dock reading as the same system, not a one-off.
const FONT = '"DM Sans", "Inter", "Arial", sans-serif';
const INACTIVE_TONE = "#9AA1B0";

/** One nav slot: lucide icon + label. Active state reads as a soft
 *  brand-tinted pill behind the icon plus a bolder stroke/weight — the same
 *  "tint chip" language as the KPI pills elsewhere — not just a color swap. */
const NavTab = ({ active, icon, label, badge = 0, color, hideLabel, onClick }) => {
  const tone = active ? color : INACTIVE_TONE;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-current={active ? "page" : undefined}
      className="flex flex-col items-center justify-center gap-[3px] relative bg-transparent border-0 outline-none cursor-pointer h-full active:scale-[0.94] transition-transform duration-150"
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <span
        className="relative flex items-center justify-center rounded-[12px] transition-colors duration-200"
        style={{ width: 40, height: 30, background: active ? `${color}16` : "transparent" }}
      >
        <span className="flex" style={{ color: tone }}>
          <Icon icon={icon} width="22" height="22" strokeWidth={active ? 2 : 1.5} />
        </span>
        {badge > 0 && (
          <span className="absolute -top-[3px] -right-[5px] min-w-[16px] h-[16px] px-[4px] rounded-full bg-[#EF4444] text-white text-[9px] font-semibold flex items-center justify-center border-[1.5px] border-white leading-none">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </span>
      {!hideLabel && (
        <span
          className="text-[10px] leading-none"
          style={{ color: tone, fontWeight: active ? 600 : 500, letterSpacing: "-0.01em" }}
        >
          {label}
        </span>
      )}
    </button>
  );
};

/**
 * Mobile bottom navigation — floating rounded dock with a raised center
 * profile tile. Icons are system lucide glyphs; the active tab reads via a
 * soft brand-tinted pill behind the icon (Instagram/Threads-style) instead
 * of color alone, for clearer visual hierarchy. Player-side second tab is
 * "Feed" (→ /feed); admins keep "Scorekeeper" (→ /scorekeeper/timeline).
 */
const BottomNav = ({ badges }) => {
  const navigate = useNavigate();
  const superSmallScreen = useMediaQuery("(max-width:350px)");
  const { primaryColor } = UseCompanyColors();
  const user = UseUserDetails();

  // Mobile-only Facebook-style auto-hide: slides off-screen (down) on scroll
  // down, reappears on any scroll up. The dock is already `md:hidden` on
  // desktop, so gate the scroll listener itself to mobile too.
  const isMobileView = useMediaQuery("(max-width:767px)");
  const hidden = useHideOnScroll(8, isMobileView);

  const location = useLocation();
  const paths = (location.pathname || "").split("/");
  const currentPath = paths[paths.length - 1];

  const isDashboard = currentPath === "home";
  const isFeed = paths.includes("feed");
  const isClaims = currentPath === "claims";
  const isHub = currentPath === "hub";

  const brand = primaryColor || "#1B598E";

  return (
    <div
      className="fixed z-[1000] left-[14px] right-[14px] bottom-[14px] rounded-[22px] grid grid-cols-5 items-center md:hidden"
      style={{
        minHeight: 66,
        // Respect the home-indicator safe area on notched phones instead of
        // sitting flush against it.
        paddingBottom: "env(safe-area-inset-bottom)",
        background: "#fff",
        border: "1px solid #EFF1F5",
        // Same layered "premium" shadow recipe as FeedCard's post cards
        // (soft inner highlight + tight contact shadow + long ambient falloff).
        boxShadow: "0 1px 0 rgba(255,255,255,0.7) inset, 0 1px 2px rgba(15,23,42,0.04), 0 20px 40px -14px rgba(15,23,42,0.22)",
        fontFamily: FONT,
        // 100% only clears the dock's own box. The center profile circle is
        // raised translateY(-14px) above that box (plus its shadow blur), so
        // the hide distance needs extra headroom on top of the dock's own
        // bottom-[14px] offset or that circle's tip stays poking into view.
        transform: hidden ? "translateY(calc(100% + 48px))" : "translateY(0)",
        transition: "transform 0.3s ease-in-out",
        willChange: "transform",
      }}
    >
      <NavTab
        active={isDashboard}
        icon="lucide:layout-grid"
        label="Dashboard"
        color={brand}
        hideLabel={superSmallScreen}
        onClick={() => navigate("/home")}
      />
      <NavTab
        active={isFeed}
        icon="lucide:newspaper"
        label="Feed"
        color={brand}
        hideLabel={superSmallScreen}
        onClick={() => navigate("/feed")}
      />

      {/* Center — raised profile photo → account settings, ringed in the
          brand color so it reads as the premium/accent slot in the dock. */}
      <div className="flex justify-center">
        <div
          onClick={() => navigate("/account-settings")}
          className="flex items-center justify-center cursor-pointer overflow-hidden active:scale-[0.94] transition-transform duration-150"
          style={{
            width: 54,
            height: 54,
            borderRadius: "50%",
            transform: "translateY(-14px)",
            background: "#fff",
            border: "3px solid #fff",
            boxShadow: `0 0 0 3px ${brand}26, 0 10px 20px -6px rgba(16,24,40,0.32)`,
          }}
        >
          {user.image ? (
            <img src={GET_IMAGE(user.image)} alt="Profile" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-indigo-400 flex items-center justify-center font-bold text-white text-sm">
              {user.firstName?.[0]}{user.lastName?.[0]}
            </div>
          )}
        </div>
      </div>

      <NavTab
        active={isClaims}
        icon="lucide:clipboard-list"
        label="Claims"
        color={brand}
        badge={badges?.claims || 0}
        hideLabel={superSmallScreen}
        onClick={() => navigate("/claims")}
      />
      <NavTab
        active={isHub}
        icon="lucide:share-2"
        label="Hub"
        color={brand}
        hideLabel={superSmallScreen}
        onClick={() => navigate("/hub")}
      />
    </div>
  );
};

export default BottomNav;
